/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import {
  buildSort,
  buildCriterionExpression,
  buildResultList,
  buildRelevanceSortCriterion,
  buildFieldSortCriterion,
  SortOrder,
  Sort as HeadlessSort,
  SortCriterion,
  SearchAppState,
} from '@coveo/headless';
import { SvgIcon } from 'helpers/SvgIcon';
import useDictionary from 'lib/hooks/useDictionary';
import {
  useCoveoEngine,
  useCoveoEngineControls,
} from 'lib/utils/coveo/SearchContext/CoveoEngineContext';
import { useSearch } from 'lib/utils/coveo/SearchContext/SearchContext';

interface SortProps {
  onSortChange?: (newResults: SearchAppState['search']) => void;
  enableAZSort?: boolean;
  azSortField?: string;
  enableNewestSort?: boolean;
  newestSortField?: string;
  enableDistanceSort?: boolean; // Optional distance sorting
  fieldsToInclude?: string[];
  finalCoordinates?: { latitude: number; longitude: number } | null;
  setNewestAsDefault?: boolean;
}

// Helper: check if distance sort should be applied
const shouldEnableDistanceSort = (
  enableDistanceSort: boolean,
  finalCoordinates: { latitude: number; longitude: number } | null
) => enableDistanceSort && finalCoordinates?.latitude && finalCoordinates?.longitude;

const Sort: React.FC<SortProps> = ({
  onSortChange,
  enableAZSort = false,
  azSortField = 'cwtitle',
  enableNewestSort = false,
  newestSortField = 'cwblogdate',
  enableDistanceSort = false,
  finalCoordinates = null,
  fieldsToInclude = [],
  setNewestAsDefault = false,
}) => {
  const { getDictionaryValue } = useDictionary();
  const engine = useCoveoEngine();
  const engineControls = useCoveoEngineControls();
  const { setContextResults } = useSearch();

  const defaultCriterion = buildRelevanceSortCriterion();

  // Apply distance sort only if condition is met
  const applyDistanceSort = shouldEnableDistanceSort(enableDistanceSort, finalCoordinates);

  // Optional initial distance-based sorting (but not shown in dropdown)
  const initialCriterion = applyDistanceSort
    ? buildFieldSortCriterion('distanceinmiles', SortOrder.Ascending)
    : setNewestAsDefault && enableNewestSort && newestSortField
      ? buildFieldSortCriterion(newestSortField, SortOrder.Descending)
      : defaultCriterion;

  const [sort] = useState<HeadlessSort>(() =>
    engine
      ? buildSort(engine, {
          initialState: {
            criterion: initialCriterion,
          },
        })
      : ({} as HeadlessSort)
  );

  const [resultList] = useState(() =>
    engine
      ? buildResultList(engine, {
          options: {
            fieldsToInclude,
          },
        })
      : ({} as any)
  );

  const [state, setState] = useState(() =>
    engine ? sort.state : { sortCriteria: '', hasNextSortCriteria: false }
  );

  const dropdownCriteria: [string, SortCriterion][] = [['Relevancy', defaultCriterion]];
  if (enableAZSort && azSortField) {
    dropdownCriteria.push(['A–Z', buildFieldSortCriterion(azSortField, SortOrder.Ascending)]);
  }
  if (enableNewestSort && newestSortField) {
    dropdownCriteria.push([
      'Newest',
      buildFieldSortCriterion(newestSortField, SortOrder.Descending),
    ]);
  }
  const dropdownDistanceCriterion = buildFieldSortCriterion('distanceinmiles', SortOrder.Ascending);
  // if (enableDistanceSort) {
  //   dropdownCriteria.push(['Distance', dropdownDistanceCriterion]);
  // }

  // useEffect(() => {
  //   if (!engine) return;
  //   engine.executeFirstSearch();
  // }, [engine]);

  useEffect(() => {
    if (!engine || !sort.subscribe || !resultList.subscribe) return;

    const unsubscribeSort = sort.subscribe(() => setState(sort.state));
    const unsubscribeResults = resultList.subscribe(() => {
      const fullSearchState = engine.state.search;
      onSortChange?.(fullSearchState);
      setContextResults(fullSearchState);
    });

    return () => {
      unsubscribeSort?.();
      unsubscribeResults?.();
    };
  }, [sort, resultList, onSortChange, engine, setContextResults]);

  if (!engine) return null;

  const getCriterionFromName = (name: string) =>
    dropdownCriteria.find(([label]) => label === name)?.[1] ?? dropdownCriteria[0][1];

  const getCurrentCriterion = () =>
    dropdownCriteria.find(
      ([, criterion]) => state.sortCriteria === buildCriterionExpression(criterion)
    ) ?? dropdownCriteria[0];

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newCriterion = getCriterionFromName(e.target.value);
    // Mark that the user manually changed sort so auto-distance won't override
    try {
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem('coveoUserSortOverride', '1');
      }
    } catch {}

    // Toggle distance features based on selected sort
    const isDistance =
      buildCriterionExpression(newCriterion) ===
      buildCriterionExpression(dropdownDistanceCriterion);
    engineControls?.setDistanceOverride(isDistance ? 'on' : 'off');
    sort.sortBy(newCriterion);
  };

  return (
    <div className="sort flex items-center py-5">
      <p className="text-16 text-gray-900 leading-[25px] font-nunitoSans" id="sort-label">
        {`${getDictionaryValue('SortBy')} :`}
      </p>
      <div className="relative flex items-center">
        <select
          className="text-16 text-gray-900 leading-[25px] font-nunitoSans appearance-none relative z-[1] bg-transparent pl-3 pr-12 cursor-pointer"
          value={getCurrentCriterion()[0]}
          onChange={handleChange}
          id="sort-dropdown"
          name="sort-dropdown"
          aria-labelledby="sort-label"
        >
          {dropdownCriteria.map(([label]) => (
            <option key={label} value={label}>
              {label}
            </option>
          ))}
        </select>
        <SvgIcon
          className="ml-4 [&>path]:fill-blue-base absolute right-0"
          size="md"
          icon="chevron-down"
        />
      </div>
    </div>
  );
};

export default Sort;
